import { focusNewPage } from '@crown/framework/a11y';
import { flushSync, hydrate, mount } from '@crown/framework/dom';
import {
  type Instruction,
  isFunction,
  isInstruction,
} from '@crown/framework/ir';
import { Signal } from '@crown/signal';
import { effect } from '@crown/ui/effect';
import { Alert } from '@crown/ui/alert';
import type {
  ComponentDataContext,
  ComponentLifecycleManager,
  OutletContent,
  RouteConfig,
  RouteMeta,
  RouterState,
} from '../types';
import {
  joinPaths,
  matchPath,
  ROUTE_ATTRIBUTE,
  resolveComponent,
  routeKey,
} from './match';

/** An outlet element carrying the cleanup and render bookkeeping the router attaches to it. */
interface RouterManagedElement extends HTMLElement {
  _routerCleanup?: (() => void) | null;
  _routerRenderVersion?: number;
}

/** A child of an outlet that stores its own cleanup on itself. */
interface SelfCleaningElement extends Element {
  _cleanup?: (() => void) | null;
}

/** Whether an outlet's child stores its own cleanup on itself, as some components do. */
function isSelfCleaning(child: Element): child is SelfCleaningElement {
  return '_cleanup' in child;
}

/** What an outlet shows when its component fails; the error text is text, never markup. */
function outletProblem(
  title: string,
  message: string,
  tone: 'crashed' | 'failed',
): Instruction {
  return Alert({
    variant: tone === 'crashed' ? 'danger' : 'warning',
    title,
    message,
  });
}

/** Replaces what an outlet shows with an instruction, and returns what undoes its bindings. */
function mountInto(el: HTMLElement, node: Instruction): () => void {
  el.replaceChildren();
  const dispose = mount(node, el);
  flushSync();
  return dispose;
}

/**
 * Adopts the server's HTML for this route when the outlet holds it, and mounts
 * otherwise. The mark is taken off either way: only the page load's content
 * came from the server, and every later navigation mounts. HTML that does not
 * match what the page builds is replaced by a fresh mount, which is the
 * page's recovery.
 */
export function adoptInto(
  el: HTMLElement,
  node: Instruction,
  path: string,
): () => void {
  const rendered = el.getAttribute(ROUTE_ATTRIBUTE);
  if (rendered === null) return mountInto(el, node);
  el.removeAttribute(ROUTE_ATTRIBUTE);
  if (routeKey(rendered) !== routeKey(path) || el.childNodes.length === 0)
    return mountInto(el, node);
  try {
    const dispose = hydrate(node, el);
    flushSync();
    return dispose;
  } catch (error) {
    /*
      Any failure to adopt, not only a reported mismatch: the browser's parser
      can move what the server wrote (a block inside a paragraph, a row
      outside its table), and the hydrator then meets markers it cannot pair.
      The page is still the page; building it afresh is the recovery.
    */
    const reason = error instanceof Error ? error.message : String(error);
    console.warn(
      '[Router] the server HTML could not be adopted; mounting the page afresh:',
      reason,
    );
    return mountInto(el, node);
  }
}

export function createRouterCore() {
  const state: RouterState = {
    currentRoute: new Signal.State(''),
    routeParams: new Signal.State({}),
    routeData: new Signal.State<RouteConfig | null>(null),
    routeComponent: new Signal.State(null),
    isLoading: new Signal.State(false),
    error: new Signal.State(null),
    outlets: new Signal.State({ '': null }),
    // Incremented just before outlets.set() so effects fire after routeDataSignal is updated.
    renderVersion: new Signal.State(0),
    lifecycle: {
      onInit: new Signal.State(null),
      onDestroy: new Signal.State(null),
    },
    isTransitioning: new Signal.State(false),
  };
  const outletElements: Record<string, HTMLElement> = {};
  const outletEffects: Record<string, () => void> = {};
  const outletContent: Record<
    string,
    Signal.Computed<{
      component: OutletContent | null | undefined;
      renderVersion: number;
    }>
  > = {};
  const outletRenderedContent: Record<string, OutletContent> = {};
  const outletRenderErrors: Record<string, number> = {};
  // The first render is the page load, which the browser already announces;
  // after that each new page in the main outlet takes focus and is announced.
  const outletHasRendered: Record<string, boolean> = {};
  const MAX_RENDER_ERRORS = 3;

  function setupOutletElements() {
    const elements = document.querySelectorAll<HTMLElement>('[router-outlet]');

    for (const cleanup of Object.values(outletEffects)) {
      cleanup();
    }
    Object.keys(outletEffects).forEach((k) => delete outletEffects[k]);
    Object.keys(outletContent).forEach((k) => delete outletContent[k]);
    Object.keys(outletRenderedContent).forEach(
      (k) => delete outletRenderedContent[k],
    );
    Object.keys(outletRenderErrors).forEach(
      (k) => delete outletRenderErrors[k],
    );

    for (const element of Array.from(elements)) {
      const name = element.getAttribute('router-outlet') || '';
      outletElements[name] = element;

      outletContent[name] = new Signal.Computed(
        () => {
          const outlets = state.outlets.get();
          const component = outlets[name];
          const renderVersion = state.renderVersion.get();
          return { component, renderVersion };
        },
        {
          equals: (oldVal, newVal) => {
            return (
              oldVal?.component === newVal?.component &&
              oldVal?.renderVersion === newVal?.renderVersion
            );
          },
        },
      );

      outletEffects[name] = effect(() => {
        try {
          const computed = outletContent[name];
          if (!computed) return;

          const { component: content, renderVersion } = computed.get();
          console.info(`[Router] outlet effect fired for "${name}"`, {
            hasContent: content != null,
            renderVersion,
          });
          const el: RouterManagedElement | undefined = outletElements[name];

          if (!el || content == null) return;

          const errorCount = outletRenderErrors[name] || 0;
          if (errorCount >= MAX_RENDER_ERRORS) {
            console.error(
              `[Router] Outlet "${name}" has failed ${errorCount} times, stopping renders to prevent loop`,
            );
            el._routerCleanup = mountInto(
              el,
              outletProblem(
                'Component error',
                `This component has crashed ${errorCount} times. Check the console for details.`,
                'crashed',
              ),
            );
            return;
          }

          try {
            Signal.subtle.untrack(() => {
              const lastRenderedContent = outletRenderedContent[name];
              const lastRenderVersion = el._routerRenderVersion;
              if (
                lastRenderedContent === content &&
                lastRenderVersion === renderVersion
              ) {
                return;
              }

              const existingCleanup = el._routerCleanup;
              if (existingCleanup) {
                try {
                  existingCleanup();
                } catch (err) {
                  console.error(
                    `[Router] Cleanup error for outlet "${name}":`,
                    err,
                  );
                }
                el._routerCleanup = null;
              }

              // Also call _cleanup on direct children (e.g. components that store cleanup on themselves)
              for (const child of Array.from(el.children)) {
                if (!isSelfCleaning(child)) continue;
                const childCleanup = child._cleanup;
                if (isFunction(childCleanup)) {
                  try {
                    childCleanup();
                  } catch (err) {
                    console.error(
                      `[Router] Child cleanup error for outlet "${name}":`,
                      err,
                    );
                  }
                  child._cleanup = null;
                }
              }

              renderToOutlet(el, content);
              outletRenderedContent[name] = content;
              el._routerRenderVersion = renderVersion;
              if (name === '' && outletHasRendered[name]) focusNewPage(el);
              outletHasRendered[name] = true;
            });

            outletRenderErrors[name] = 0;
          } catch (err) {
            console.error(`[Router] Render error for outlet "${name}":`, err);
            outletRenderErrors[name] = errorCount + 1;
            el._routerCleanup = mountInto(
              el,
              outletProblem(
                'Render error',
                err instanceof Error ? err.message : String(err),
                'failed',
              ),
            );
          }
        } catch (err) {
          console.error(`[Router] Effect error for outlet "${name}":`, err);
        }
      });
    }
  }
  function renderToOutlet(el: RouterManagedElement, content: OutletContent) {
    if (isInstruction(content)) {
      el._routerCleanup = adoptInto(el, content, state.currentRoute.get());
      return;
    }

    try {
      const dataContext: ComponentDataContext<unknown> = {
        params: new Signal.State<Record<string, string | number | boolean>>(
          state.routeParams.get(),
        ),
        meta: new Signal.State<RouteMeta>(state.routeData.get()?.meta || {}),
        data: state.routeDataSignal || new Signal.State<unknown>(null),
        loadData: state.routeLoadDataFunction || null,
        route: new Signal.State<RouteConfig | null>(state.routeData.get()),
      };
      const cleanup = new Set<() => void>();
      const lifecycle: ComponentLifecycleManager = {
        phase: new Signal.State<
          'created' | 'mounted' | 'destroying' | 'destroyed'
        >('mounted'),
        isActive: new Signal.State(true),
        onInit: new Signal.State(false),
        onDestroy: new Signal.State(false),
        effect: (fn: () => void | (() => void)) => {
          const c = effect(() => {
            if (lifecycle.isActive.get() && lifecycle.phase.get() === 'mounted')
              return fn();
          });
          cleanup.add(c);
          return () => {
            cleanup.delete(c);
            c();
          };
        },
        onCleanup: (fn: () => void) => cleanup.add(fn),
        destroy: () => {
          lifecycle.phase.set('destroying');
          lifecycle.isActive.set(false);
          lifecycle.onDestroy.set(true);
          cleanup.forEach((fn) => {
            try {
              fn();
            } catch {}
          });
          cleanup.clear();
          lifecycle.phase.set('destroyed');
        },
      };
      queueMicrotask(() => lifecycle.onInit.set(true));

      const result = content(dataContext, lifecycle);
      el._routerCleanup = lifecycle.destroy;

      // The page's bindings stop when the router leaves it.
      cleanup.add(adoptInto(el, result, state.currentRoute.get()));
    } catch (err) {
      console.error('[Router] Component function threw error:', err);
      throw err;
    }
  }
  function buildUrlWithParams(
    pattern: string,
    params: Record<string, string | number | boolean>,
  ) {
    let url = pattern;
    for (const [k, v] of Object.entries(params))
      url = url.replace(`:${k}`, encodeURIComponent(String(v)));
    return url;
  }
  /**
   * Dispose every outlet this router owns.
   *
   * The effects an outlet registers live for as long as the page, and until now nothing
   * could stop them: a second router created over the same document kept the first one's
   * effects running against elements it no longer owned. That is invisible in an
   * application, which builds one router and keeps it, and very visible anywhere that
   * builds several — a test file, or a host that tears a routed area down and rebuilds it.
   *
   * Unmounts each outlet's content through the same cleanup path a navigation uses, so a
   * component's onCleanup runs exactly as it would when navigating away.
   */
  function destroyOutlets() {
    for (const cleanup of Object.values(outletEffects)) {
      try {
        cleanup();
      } catch (error) {
        console.error('[Router] Outlet effect cleanup failed:', error);
      }
    }
    for (const [name, el] of Object.entries<RouterManagedElement>(
      outletElements,
    )) {
      const existingCleanup = el?._routerCleanup;
      if (existingCleanup) {
        try {
          existingCleanup();
        } catch (error) {
          console.error('[Router] Component cleanup failed:', error);
        }
        el._routerCleanup = null;
      }
      delete outletRenderedContent[name];
    }
    Object.keys(outletEffects).forEach((k) => delete outletEffects[k]);
    Object.keys(outletContent).forEach((k) => delete outletContent[k]);
    Object.keys(outletElements).forEach((k) => delete outletElements[k]);
    Object.keys(outletRenderErrors).forEach(
      (k) => delete outletRenderErrors[k],
    );
    Object.keys(outletHasRendered).forEach((k) => delete outletHasRendered[k]);
  }

  return {
    state,
    setupOutletElements,
    destroyOutlets,
    renderToOutlet,
    matchPath,
    buildUrlWithParams,
    resolveComponent,
    joinPaths,
  };
}
